import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { 
  MenuUnfoldOutlined, 
  MenuFoldOutlined,
  LogoutOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { logoutReducer } from '../../Store/Slices/AuthSlice';

const Header = ({ toggleCollapsed, collapsed }) => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  
  // State for hover effects
  const [logoHovered, setLogoHovered] = useState(false);
  const [toggleHovered, setToggleHovered] = useState(false);
  const [userBadgeHovered, setUserBadgeHovered] = useState(false);
  const [logoutHovered, setLogoutHovered] = useState(false);
  
  // Mobile breakpoint detection
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  return (
    <div className="modern-header" style={{ 
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: isMobile ? '56px' : '64px',
      padding: isMobile ? '0 8px' : '0 16px',
      background: '#ffffff',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
    }}>
      {/* Left side: Logo and toggle button */}
      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
        {/* Logo first at the edge of the screen */}
        <img 
          src={user?.logo || 'https://via.placeholder.com/150x40?text=ISIQALO'} 
          alt="Company Logo" 
          style={{ 
            height: isMobile ? '32px' : '40px', 
            marginRight: isMobile ? '8px' : '16px',
            transition: 'all 0.25s ease',
            transform: logoHovered ? 'scale(1.05)' : 'scale(1)',
            filter: logoHovered ? 'brightness(1.1)' : 'brightness(1)',
            maxWidth: isMobile ? '100px' : 'none'
          }} 
          onMouseEnter={() => setLogoHovered(true)}
          onMouseLeave={() => setLogoHovered(false)}
        />

        {/* Toggle button with dynamic hover styling */}
        <Button
          type="text"
          icon={collapsed ? 
            <MenuUnfoldOutlined style={{ fontSize: isMobile ? '14px' : '16px' }} /> : 
            <MenuFoldOutlined style={{ fontSize: isMobile ? '14px' : '16px' }} />
          }
          onClick={toggleCollapsed}
          style={{ 
            color: '#043f6e',
            fontSize: isMobile ? '14px' : '16px',
            marginLeft: isMobile ? '4px' : '79px',
            background: toggleHovered ? 'rgba(76, 119, 236, 0.15)' : 'rgba(98, 135, 184, 0.07)',
            border: 'none',
            borderRadius: '4px',
            padding: isMobile ? '4px 8px' : '6px 12px',
            transition: 'all 0.25s ease',
            minWidth: isMobile ? '32px' : 'auto'
          }}
          onMouseEnter={() => setToggleHovered(true)}
          onMouseLeave={() => setToggleHovered(false)}
        />
      </div>

      {/* Right side: User info and logout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        flexShrink: 0
      }}>
        {/* User badge with icon and hover effect */}
        {!isMobile ? (
          <div 
            style={{ 
              background: userBadgeHovered ? '#a17c1f' : '#cc9414',
              color: 'white',
              padding: '0 12px',
              borderRadius: '4px',
              marginRight: '16px',
              fontSize: '14px',
              fontWeight: '500',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.25s ease',
              boxShadow: userBadgeHovered ? '0 0 8px rgba(231, 217, 88, 0.6)' : 'none',
              transform: userBadgeHovered ? 'translateY(-1px)' : 'translateY(0)'
            }}
            onMouseEnter={() => setUserBadgeHovered(true)}
            onMouseLeave={() => setUserBadgeHovered(false)}
          >
            <UserOutlined style={{ 
              marginRight: '8px', 
              fontSize: '14px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              padding: '4px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }} />
            {isMobile ? (user?.name || 'User') : `${user?.name || 'User'} | ${user?.role || 'Role'} | ${user?.outletName || 'ISIQALO WATERFRONT LODGE'}`}
          </div>
        ) : (
          <div 
            style={{ 
              background: userBadgeHovered ? '#a17c1f' : '#cc9414',
              color: 'white',
              padding: '0 8px',
              borderRadius: '4px',
              marginRight: '8px',
              fontSize: '12px',
              fontWeight: '500',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.25s ease',
              boxShadow: userBadgeHovered ? '0 0 8px rgba(231, 217, 88, 0.6)' : 'none',
              transform: userBadgeHovered ? 'translateY(-1px)' : 'translateY(0)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              
              width: '140px',
              textOverflow: 'ellipsis'
            }}
            onMouseEnter={() => setUserBadgeHovered(true)}
            onMouseLeave={() => setUserBadgeHovered(false)}
          >
            <UserOutlined style={{ 
              marginRight: '4px', 
              fontSize: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              padding: '2px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }} />
            <span style={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {user?.name || 'User'}
            </span>
          </div>
        )}
        
        {/* Logout button with brighter red hover */}
        <Button 
          danger
          icon={<LogoutOutlined />}
          onClick={() => dispatch(logoutReducer())}
          style={{ 
            background: logoutHovered ? '#04356e' : '#043f6e',
            color: 'white',
            border: 'none',
            height: isMobile ? '28px' : '32px',
            display: 'flex',
            alignItems: 'center',
            boxShadow: logoutHovered ? '0 0 8px rgba(51, 102, 197, 0.5)' : 'none',
            transform: logoutHovered ? 'translateY(-1px)' : 'translateY(0)',
            transition: 'all 0.25s ease',
            fontSize: isMobile ? '12px' : '14px',
            padding: isMobile ? '0 8px' : '0 12px',
            minWidth: isMobile ? '60px' : 'auto'
          }}
          onMouseEnter={() => setLogoutHovered(true)}
          onMouseLeave={() => setLogoutHovered(false)}
        >
          {isMobile ? '' : 'Log out'}
        </Button>
      </div>
    </div>
  );
};

export default Header;